import React, { useState, useEffect, useMemo } from 'react';
import { toolboxService } from '../services/api';
import dayjs from 'dayjs';

const DOCTOR_COLUMNS = [
  { key: 'doctor_id', label: 'Doctor ID', align: 'left', numeric: false },
  { key: 'specialty_id', label: 'Specialty', align: 'left', numeric: false },
  { key: 'district_id', label: 'District', align: 'left', numeric: false },
  { key: 'impression', label: 'Impression', align: 'right', numeric: true },
  { key: 'average_idx', label: 'Avg Idx', align: 'right', numeric: true },
  { key: 'average_search_district', label: 'Avg Districts', align: 'right', numeric: true },
  { key: 'selection', label: 'Selection', align: 'right', numeric: true },
  { key: 'appointment', label: 'Appointment', align: 'right', numeric: true },
];

const CLINIC_COLUMNS = [
  { key: 'clinic_id', label: 'Clinic ID', align: 'left', numeric: false },
  { key: 'specialty_id', label: 'Specialty', align: 'left', numeric: false },
  { key: 'district_id', label: 'District', align: 'left', numeric: false },
  { key: 'impression', label: 'Impression', align: 'right', numeric: true },
  { key: 'average_idx', label: 'Avg Idx', align: 'right', numeric: true },
  { key: 'average_search_district', label: 'Avg Districts', align: 'right', numeric: true },
];

const SortableHeader = ({ column, sortColumn, sortDirection, onSort }) => {
  const isActive = sortColumn === column.key;
  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${column.align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(column.key)}
    >
      <span className="inline-flex items-center gap-1">
        {column.label}
        {isActive && (
          <span className="text-gray-700">{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </span>
    </th>
  );
};

const DATA_START_DATE = '2026-01-23';

const FindDocAgentAnalytics = () => {
  const [clid, setClid] = useState('DEMO0010');
  const [startDate, setStartDate] = useState(dayjs().subtract(15, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
  const [dataStartDate, setDataStartDate] = useState(null);
  const [dataEndDate, setDataEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [sortColumn, setSortColumn] = useState('impression');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);

  const fetchSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await toolboxService.getFinddocAnalyticsSummary({
        clid: clid || undefined,
        start_date: startDate,
        end_date: endDate
      });

      if (result.success === false) {
        setError(result.error || 'Failed to fetch analytics');
        setData(null);
        setSummary(null);
        setPagination(null);
        setDataStartDate(null);
        setDataEndDate(null);
      } else {
        setData(result.data || []);
        setSummary(result.summary || null);
        setPagination(result.pagination || null);
        setDataStartDate(result.start_date || null);
        setDataEndDate(result.end_date || null);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setData(null);
      setSummary(null);
      setPagination(null);
      setDataStartDate(null);
      setDataEndDate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('desc');
    }
  };

  const doctorCount = useMemo(() => (data || []).filter((r) => r.type === 'doctor').length, [data]);
  const clinicCount = useMemo(() => (data || []).filter((r) => r.type === 'clinic').length, [data]);

  const columns = activeTab === 'doctor' ? DOCTOR_COLUMNS : CLINIC_COLUMNS;

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((row) => row.type === activeTab);
  }, [data, activeTab]);

  const sortedData = useMemo(() => {
    if (!filteredData.length) return [];
    const col = columns.some((c) => c.key === sortColumn) ? sortColumn : columns[0]?.key || 'impression';
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[col];
      const bVal = b[col];
      const aNum = typeof aVal === 'number';
      const bNum = typeof bVal === 'number';
      if (aNum && bNum) {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = aVal ?? '';
      const bStr = bVal ?? '';
      const cmp = String(aStr).localeCompare(String(bStr), undefined, { numeric: true });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredData, sortColumn, sortDirection, columns]);

  const districtName = (id) => summary?.district_id_to_name?.[id] ?? id;
  const specialtyName = (id) => summary?.specialty_id_to_name?.[id] ?? id;

  const renderCell = (col, row) => {
    const val = row[col.key];
    if (col.key === 'doctor_id' && val != null) {
      return (
        <a href={`https://www.finddoc.com/doctors/${val}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {val}
        </a>
      );
    }
    if (col.key === 'clinic_id' && val != null) {
      return (
        <a href={`https://www.finddoc.com/practices/${val}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {val}
        </a>
      );
    }
    if (col.key === 'specialty_id') return (val != null && summary?.specialty_id_to_name?.[val] != null) ? specialtyName(val) : 'Unknown';
    if (col.key === 'district_id') return (val != null && summary?.district_id_to_name?.[val] != null) ? districtName(val) : 'Unknown';
    return val ?? '-';
  };

  const renderTable = () => (
    <div className="overflow-x-auto border border-gray-300 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <SortableHeader
                key={col.key}
                column={col}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.map((row, idx) => (
            <tr key={`${row.type}-${row.doctor_id ?? row.clinic_id}-${idx}`} className="hover:bg-gray-50">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {renderCell(col, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">FindDoc Agent Analytics</h1>
          <p className="text-gray-600">View impressions, selections, and appointments by doctor and clinic.</p>
          <p className="text-sm text-amber-700 mt-1">Data starts from 23 Jan 2026.</p>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="start-date" className="text-sm font-medium text-gray-700">Start</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={DATA_START_DATE}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="end-date" className="text-sm font-medium text-gray-700">End</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={dayjs().subtract(1, 'day').format('YYYY-MM-DD')}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            />
          </div>
          <span className="text-sm text-gray-500">Default: 過去15天 (至昨天)</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading analytics...</p>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                onClick={() => setActiveTab('summary')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'summary'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Summary
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('doctor')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'doctor'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Doctors ({doctorCount})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('clinic')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === 'clinic'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Clinics ({clinicCount})
              </button>
            </div>

            {(dataStartDate || dataEndDate) && (
              <div className="mb-4 text-sm text-gray-600">
                Data period: <span className="font-medium">{dataStartDate ?? '-'}</span> to <span className="font-medium">{dataEndDate ?? '-'}</span>
                {activeTab === 'doctor' && (
                  <span className="block mt-1 text-gray-500 italic">
                    Remark: For now, specialty and district of the doctor are selected using the mode value (most frequently occurred).
                  </span>
                )}
              </div>
            )}

            {activeTab === 'summary' && summary?.time_series?.length > 0 && (
              <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="bg-slate-100 px-6 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Unique Conversations by Week</h3>
                  <div className="mt-1 flex gap-6 text-sm">
                    <span><span className="font-medium text-gray-900">{summary.total_unique_conversations ?? 0}</span> Total unique</span>
                    <span><span className="font-medium text-amber-700">{summary.doctors?.unique_conversations ?? 0}</span> Doctors</span>
                    <span><span className="font-medium text-blue-700">{summary.clinics?.unique_conversations ?? 0}</span> Clinics</span>
                  </div>
                </div>
                <div className="p-6 overflow-x-auto">
                  <div className="flex items-end gap-2 min-h-[220px]" style={{ minWidth: 'max-content' }}>
                    {(summary.time_series || []).filter((w) => (w.total ?? 0) > 0).map((w) => {
                      const maxVal = Math.max(1, ...summary.time_series.map((x) => x.total ?? 0));
                      const totalVal = w.total ?? 0;
                      const totalH = maxVal > 0 ? (150 * totalVal / maxVal) : 0;
                      return (
                        <div key={w.week_start} className="flex flex-col items-center w-12 shrink-0">
                          <div className="flex flex-col items-end justify-end h-[180px] w-full">
                            <div className="flex flex-col items-center">
                              <span className="text-xs font-medium text-gray-900">{totalVal}</span>
                              <div className="w-8 bg-gray-300 rounded-t mt-1" style={{ height: `${Math.max(totalH, 4)}px` }} title={`Total: ${totalVal}`} />
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-500 mt-2 text-center">
                            {dayjs(w.week_start).format('MM/DD')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-6 mt-4 text-xs text-gray-600">
                    <span><span className="inline-block w-3 h-3 rounded bg-gray-300 align-middle" /> Total unique</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'summary' ? (
              summary ? (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="bg-slate-100 px-6 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Clinics</h3>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Conversations</div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">{summary.clinics?.unique_conversations ?? 0}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Impression</div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">{summary.clinics?.total_impression ?? 0}</div>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">By District</div>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">District</th>
                                  <th className="px-3 py-2 text-right font-medium text-gray-600">Person</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(summary.clinics?.by_district || []).map((r) => (
                                  <tr key={r.id}>
                                    <td className="px-3 py-2 text-gray-800">{r.name}</td>
                                    <td className="px-3 py-2 text-right font-medium">{r.person ?? r.impression ?? 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">By Specialty</div>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Specialty</th>
                                  <th className="px-3 py-2 text-right font-medium text-gray-600">Person</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(summary.clinics?.by_specialty || []).map((r) => (
                                  <tr key={r.id}>
                                    <td className="px-3 py-2 text-gray-800">{r.name}</td>
                                    <td className="px-3 py-2 text-right font-medium">{r.person ?? r.impression ?? 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="bg-slate-100 px-6 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Doctors</h3>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Conversations</div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">{summary.doctors?.unique_conversations ?? 0}</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Impression</div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">{summary.doctors?.total_impression ?? 0}</div>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">By District</div>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">District</th>
                                  <th className="px-3 py-2 text-right font-medium text-gray-600">Person</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(summary.doctors?.by_district || []).map((r) => (
                                  <tr key={r.id}>
                                    <td className="px-3 py-2 text-gray-800">{r.name}</td>
                                    <td className="px-3 py-2 text-right font-medium">{r.person ?? r.impression ?? 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">By Specialty</div>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Specialty</th>
                                  <th className="px-3 py-2 text-right font-medium text-gray-600">Person</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(summary.doctors?.by_specialty || []).map((r) => (
                                  <tr key={r.id}>
                                    <td className="px-3 py-2 text-gray-800">{r.name}</td>
                                    <td className="px-3 py-2 text-right font-medium">{r.person ?? r.impression ?? 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="space-y-3 pt-4 border-t border-gray-200">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Funnel</div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-[80px] bg-blue-100 rounded-lg px-4 py-3 text-center">
                              <div className="text-lg font-bold text-blue-900">{summary.doctors?.total_impression ?? 0}</div>
                              <div className="text-xs text-blue-700">Total Impression</div>
                            </div>
                            <span className="text-gray-400">&#8594;</span>
                            <div className="flex-1 min-w-[80px] bg-amber-100 rounded-lg px-4 py-3 text-center">
                              <div className="text-lg font-bold text-amber-900">{summary.doctors?.total_selection ?? 0}</div>
                              <div className="text-xs text-amber-700">Selection</div>
                            </div>
                            <span className="text-gray-400">&#8594;</span>
                            <div className="flex-1 min-w-[80px] bg-green-100 rounded-lg px-4 py-3 text-center">
                              <div className="text-lg font-bold text-green-900">{summary.doctors?.total_appointment ?? 0}</div>
                              <div className="text-xs text-green-700">Appointment</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No summary data available.</div>
              )
            ) : sortedData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {activeTab} data.
              </div>
            ) : (
              <>
                {pagination && (
                  <div className="mb-4 text-sm text-gray-600">
                    {pagination.total} results
                  </div>
                )}
                {renderTable()}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FindDocAgentAnalytics;
